import React, { useEffect } from 'react';
import { MemoryRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { baleWebApp } from './services/baleWebApp';
import { Layout } from './components/Layout';
import { LoadingSpinner } from './components/LoadingSpinner';
import { BalanceView } from './views/BalanceView';
import { UsageView } from './views/UsageView';
import { PlansView } from './views/PlansView';
import { TransactionView } from './views/TransactionView';
import { PurchaseView } from './views/PurchaseView';
import { HistoryView } from './views/HistoryView';
import { TranscriptDetailView } from './views/TranscriptDetailView';

const LoginView: React.FC = () => {
  const { login, isLoading } = useAuth();
  const [error, setError] = React.useState<string | null>(null);

  const handleLogin = React.useCallback(async () => {
    try {
      await login();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'خطا در احراز هویت';
      setError(message);
    }
  }, [login]);

  useEffect(() => {
    void handleLogin();
  }, [handleLogin]);

  if (isLoading) {
    return (
      <Layout showNavigation={false}>
        <LoadingSpinner message="در حال احراز هویت..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout showNavigation={false}>
        <div style={styles.errorContainer}>
          <p style={styles.errorIcon}>⚠️</p>
          <p style={styles.errorText}>{error}</p>
          <button onClick={handleLogin} style={styles.retryButton}>
            تلاش مجدد
          </button>
        </div>
      </Layout>
    );
  }

  return null;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Layout showNavigation={false}>
        <LoadingSpinner message="در حال بررسی احراز هویت..." />
      </Layout>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginView />} />

      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <Layout>
              <HistoryView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/transcript/:id"
        element={
          <ProtectedRoute>
            <Layout showNavigation={false}>
              <TranscriptDetailView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/balance"
        element={
          <ProtectedRoute>
            <Layout>
              <BalanceView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/usage"
        element={
          <ProtectedRoute>
            <Layout>
              <UsageView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <Layout>
              <PlansView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/transactions"
        element={
          <ProtectedRoute>
            <Layout>
              <TransactionView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/purchase"
        element={
          <ProtectedRoute>
            <Layout showNavigation={false}>
              <PurchaseView />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<Navigate to="/history" replace />} />
      <Route path="*" element={<Navigate to="/history" replace />} />
    </Routes>
  );
};

function getInitialRoute(): string {
  const currentPath = window.location.pathname.replace(/^\/miniapp/, '') || '/';
  const currentSearch = window.location.search || '';
  const startParam = baleWebApp.getStartParam();

  if (startParam) {
    const params = new URLSearchParams(startParam);
    const view = params.get('view');
    const routeByView: Record<string, string> = {
      history: '/history',
      balance: '/balance',
      usage: '/usage',
      plans: '/plans',
      transactions: '/transactions',
      purchase: '/purchase',
    };
    if (view && routeByView[view]) {
      params.delete('view');
      const transcriptId = params.get('id');
      if (view === 'transcript' && transcriptId) {
        return `/transcript/${transcriptId}`;
      }
      const query = params.toString();
      return query ? `${routeByView[view]}?${query}` : routeByView[view];
    }
  }

  if (currentSearch.includes('payment=') || currentSearch.includes('status=')) {
    return `/purchase${currentSearch}`;
  }

  if (currentPath !== '/') {
    return `${currentPath}${currentSearch}`;
  }

  return currentSearch ? `/history${currentSearch}` : '/history';
}

const App: React.FC = () => {
  baleWebApp.initialize();

  useEffect(() => {
    baleWebApp.initialize();
  }, []);

  return (
    <MemoryRouter initialEntries={[getInitialRoute()]}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </MemoryRouter>
  );
};

const styles: Record<string, React.CSSProperties> = {
  errorContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    padding: '3rem 1.5rem',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '3rem',
    margin: '0 0 1rem 0',
  },
  errorText: {
    fontSize: '0.875rem',
    color: '#c33',
    margin: '0 0 2rem 0',
    lineHeight: 1.6,
  },
  retryButton: {
    backgroundColor: '#c33',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};

export default App;
